// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
//
// EXTRACTION PLAN -- required by the expert-legibility rule in
// `.claude/rules/anti-ai-slop.md`: this module is past the 400-line threshold
// and this PR appends new behavior to it (the Unresolved, Approved and
// Rejected sections, the unresolved count line and the section headings).
//
// ALREADY EXTRACTED: the judgment receipts -> `./AskV2ReviewJudgmentReceipt.tsx`. The tag and
// source filter -> `./AskV2ReviewTagFilter.tsx`. Which section a finding sits
// in -> `reviewCommentGroup` in `../../utils/visibleReviewComments.ts`, held
// from the review's load by `./useLoadedReviewCommentGroups.ts`. The
// section heading, toggle and list -> `./AskV2ReviewCommentSection.tsx`.
//
// STILL PENDING: `renderComment` (one card, well past the per-function budget)
// -> `./AskV2ReviewCommentCard.tsx`. WHEN: before any further behaviour is
// appended to this module; the next PR that adds a card behaviour lands the
// extraction first.
import { ArrowsMerge, CaretUp } from "@phosphor-icons/react";
import { Badge, Text } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { useEffect, useId, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AskV2ReviewCommentJudgmentActions } from "./AskV2ReviewCommentJudgmentActions";
import { AskV2ReviewCommentSection } from "./AskV2ReviewCommentSection";
import {
  PreviouslyConfirmedSubheading,
  PreviouslyRejectedSubheading,
} from "./AskV2ReviewJudgmentReceipt";
import { AskV2ReviewRejectedHereSubheading } from "./AskV2ReviewRejectedHereSubheading";
import { AskV2ReviewRejectScopePrompt } from "./AskV2ReviewRejectScopePrompt";
import {
  AskV2ReviewTagFilter,
  type ReviewSourceFilterOption,
} from "./AskV2ReviewTagFilter";
import { CommentTagBadges } from "./CommentTagBadges";
import {
  citedRuleBrand,
  citedRuleScopeChoices,
  type RejectionScope,
} from "./rememberedSentence";
import {
  defaultActiveReviewCommentTags,
  REVIEW_COMMENT_TAG_IDS,
  type CommentWithOptionalTags,
  type ReviewCommentSourceKey,
  type ReviewCommentTagId,
} from "./reviewCommentTags";
import { useLoadedReviewCommentGroups } from "./useLoadedReviewCommentGroups";

import {
  AskPunttReviewCommentBadge,
  AskPunttReviewCommentBody,
  AskPunttReviewCommentSource,
  type AssetFile,
  formatReviewCommentTime,
  reviewCommentAuthorLabel,
  reviewCommentCollapsedSections,
  reviewCommentTitle,
} from "../../components/ask-puntt-review";
import {
  claimCitationsFor,
  placedCommentMemoryIds,
} from "../../components/ask-puntt-review/claimCitations";
import { type LinkableClaim } from "../../plugins/rehype-claim-citation";
import {
  KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
  KNOWLEDGE_DATABASE_PATH,
} from "../../routes/settings/routes/knowledge-database/search";
import {
  type LinkableMemory,
  memoryLinksFor,
} from "../../utils/memoryCitationFields";
import {
  isRejectedBefore,
  type LoadedReviewCommentGroups,
  REVIEW_COMMENT_GROUPS,
  reviewCommentGroup,
} from "../../utils/visibleReviewComments";

const SECTION_LABEL_KEYS = {
  unresolved: "askV2.review.sectionUnresolved",
  confirmed: "askV2.review.sectionConfirmed",
  rejected: "askV2.review.sectionRejected",
} as const;

type AskV2ReviewCommentsListProps = {
  comments: readonly CommentWithOptionalTags[];
  commentNumbers?: Readonly<Record<string, number>>;
  /**
   * The section each finding sat in when the review loaded. Absent, the list
   * holds its own from its first render.
   */
  commentGroups?: LoadedReviewCommentGroups;
  contextFiles?: readonly AssetFile[];
  reviewFiles?: readonly AssetFile[];
  selectedCommentId?: string | null;
  authorNameByUserId?: Readonly<Record<string, string>>;
  currentUserId?: string;
  currentUserName?: string;
  activeTags?: ReadonlySet<ReviewCommentTagId>;
  showTagFilter?: boolean;
  onToggleTag?: (tag: ReviewCommentTagId, checked: boolean) => void;
  /** The "raised by" half of the filter; absent when no expert raised a finding. */
  sourceFilter?: {
    sources: readonly ReviewSourceFilterOption[];
    hidden: ReadonlySet<ReviewCommentSourceKey>;
    onToggle: (source: ReviewCommentSourceKey, checked: boolean) => void;
  };
  judgmentPending?: boolean;
  onJudge?: (
    commentId: string,
    liked: boolean | null,
    scope?: RejectionScope,
  ) => void;
  onSelect: (commentId: string) => void;
  onClear: () => void;
};

function CommentSeverityBadge({
  severity,
}: {
  severity: CommentWithOptionalTags["severity"];
}) {
  const { t } = useTranslation("ui");
  if (!severity) return null;

  const label =
    severity === "high"
      ? t("highConfidence")
      : severity === "medium"
        ? t("mediumConfidence")
        : t("lowConfidence");

  return (
    <Badge
      size="1"
      variant="soft"
      color="gray"
      className="inline-flex items-center gap-1"
      data-testid="ask-v2-review-comment-confidence-badge"
    >
      <CaretUp size={10} weight="bold" aria-hidden="true" />
      {label}
    </Badge>
  );
}

// Room for N size-1 icon buttons (24px) with gap-2 between them.
const ACTIONS_RESERVE_CLASS: Readonly<Record<number, string>> = {
  2: "pr-16",
};

function CommentIdentityRow({
  id,
  commentNumber,
  authorLabel,
  createdAt,
  actionCount = 0,
}: {
  id: string;
  commentNumber: number;
  authorLabel: string;
  createdAt: string;
  /** Header icons drawn over the row's right edge; their room is reserved. */
  actionCount?: number;
}) {
  const reserve = ACTIONS_RESERVE_CLASS[actionCount];
  return (
    <div
      className={`flex min-w-0 items-center gap-3${reserve ? ` ${reserve}` : ""}`}
      data-testid={`ask-v2-review-comment-identity-${id}`}
    >
      <AskPunttReviewCommentBadge
        number={commentNumber}
        data-testid={`ask-v2-review-comment-index-${id}`}
      />
      <div
        className="flex min-w-0 items-center gap-2 text-[12px] text-puntt-neutral-gray-9"
        data-testid={`ask-v2-review-comment-author-${id}`}
      >
        <span className="truncate font-medium text-puntt-neutral-gray-11">
          {authorLabel}
        </span>
        <span className="shrink-0">{formatReviewCommentTime(createdAt)}</span>
      </div>
    </div>
  );
}

function CollapsedCommentPreview({
  comment,
}: {
  comment: CommentWithOptionalTags;
}) {
  const sections = reviewCommentCollapsedSections(comment.description);
  if (sections.length === 0) {
    const preview = comment.description.trim().split("\n").slice(1).join(" ");
    if (!preview) return null;
    return (
      <Text as="p" size="3" className="mt-2 text-puntt-neutral-gray-10">
        {preview}
      </Text>
    );
  }

  return (
    <div
      className="mt-2 space-y-2"
      data-testid={`ask-v2-review-comment-preview-${comment._id}`}
    >
      {sections.map((section) => (
        <Text
          as="p"
          key={section.label}
          size="3"
          className="text-puntt-neutral-gray-11"
          data-testid={`ask-v2-review-comment-section-${comment._id}-${section.label.toLowerCase()}`}
        >
          <span className="font-semibold text-puntt-neutral-gray-12">
            {section.label}:
          </span>{" "}
          {section.body}
        </Text>
      ))}
    </div>
  );
}

export function AskV2ReviewCommentsList({
  comments,
  commentNumbers: commentNumbersProp,
  commentGroups: commentGroupsProp,
  contextFiles = [],
  reviewFiles = [],
  selectedCommentId = null,
  authorNameByUserId,
  currentUserId,
  currentUserName,
  activeTags = defaultActiveReviewCommentTags(),
  showTagFilter = false,
  onToggleTag = () => undefined,
  sourceFilter,
  judgmentPending = false,
  onJudge,
  onSelect,
  onClear,
}: AskV2ReviewCommentsListProps) {
  const { t } = useTranslation("ui");
  const sectionIdBase = useId();
  const commentNumbers = useMemo(() => {
    if (commentNumbersProp) return commentNumbersProp;
    const numbers: Record<string, number> = {};
    let index = 0;
    for (const comment of comments) {
      if (comment.isDeleted) continue;
      index += 1;
      numbers[comment._id] = index;
    }
    return numbers;
  }, [commentNumbersProp, comments]);

  // The saved memories each comment rests on, numbered over the comment's own
  // text. Derived once per list, and before the claims: a memory whose sentence
  // takes a chip must not also link the claim wording it hangs on.
  const memoryCitationsByComment = useMemo(() => {
    const byId = new Map<string, LinkableMemory[]>();
    for (const comment of comments) {
      byId.set(
        comment._id,
        memoryLinksFor(comment.citations, comment.description, comment._id),
      );
    }
    return byId;
  }, [comments]);

  // Derived once per comment list, not inside the render map. Called inline it
  // handed `AskPunttReviewCommentBody` a fresh array identity on every render
  // of this list -- and that array is what `Markdown` keys its rehype-plugin
  // memo on, so every unrelated re-render (a tag filter, a selection) rebuilt
  // the unified processor for every comment on screen. `ChatMessage` already
  // memoises the same derivation; this is the list doing the same.
  const claimCitationsByComment = useMemo(() => {
    const byId = new Map<string, LinkableClaim[]>();
    for (const comment of comments) {
      const placed = placedCommentMemoryIds(
        comment.description,
        memoryCitationsByComment.get(comment._id) ?? [],
      );
      byId.set(comment._id, claimCitationsFor(comment, undefined, placed));
    }
    return byId;
  }, [comments, memoryCitationsByComment]);

  const authorOptions = {
    aiLabel: t("aiReviewer"),
    currentUserId,
    currentUserName,
    authorNameByUserId,
    unknownAuthorLabel: t("member"),
  };

  // Why the list is empty, which is not always "you filtered it". A review
  // that found nothing -- or one whose checks were all blocked -- used to read
  // "No findings match the selected types" while every type was still
  // selected, and with `showTagFilter` false the control that copy blames is
  // not even on screen. A rejection never empties it: rejected findings stay
  // in the list, grayed.
  const tagFilterNarrows =
    REVIEW_COMMENT_TAG_IDS.some((tag) => !activeTags.has(tag)) ||
    (sourceFilter?.hidden.size ?? 0) > 0;
  const emptyStateTestId = tagFilterNarrows
    ? "ask-v2-review-tag-filter-empty"
    : "ask-v2-review-no-findings-empty";
  const emptyStateCopy = tagFilterNarrows
    ? t("askV2.review.tagFilterEmpty")
    : t("askV2.review.noFindings");

  // Findings are grouped by what had been decided on them when the review
  // loaded: Unresolved holds every finding nobody had approved or rejected,
  // new in this review or raised in a past one. A judgment made in this
  // session leaves the card where it is, grayed or pressed (#5850); the next
  // load files it. All three always render, empty ones included, so
  // "Approved 0" says there is nothing approved yet rather than leaving her to
  // wonder where it went.
  const ownGroups = useLoadedReviewCommentGroups(comments);
  const loadedGroups = commentGroupsProp ?? ownGroups;
  const grouped = useMemo(() => {
    const byGroup: Record<
      (typeof REVIEW_COMMENT_GROUPS)[number],
      CommentWithOptionalTags[]
    > = { unresolved: [], confirmed: [], rejected: [] };
    for (const comment of comments) {
      const group =
        loadedGroups.get(comment._id) ?? reviewCommentGroup(comment);
      byGroup[group].push(comment);
    }
    return byGroup;
  }, [comments, loadedGroups]);
  const sections = useMemo(
    () =>
      REVIEW_COMMENT_GROUPS.map((key) => ({
        key,
        labelKey: SECTION_LABEL_KEYS[key],
        items: grouped[key],
      })),
    [grouped],
  );
  const unresolvedCount = grouped.unresolved.length;
  const showSections = comments.length > 0;
  // Only Unresolved starts open: it is the list she works through. Approved
  // and Rejected start collapsed (Surbhi, 2026-10-07), their counts still on
  // the heading, so a decided finding is one click away rather than gone.
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    () => new Set(["confirmed", "rejected"]),
  );
  // why: `sections` takes a new identity on every poll, filter change or added
  // comment; opening on that would reopen a section she closed while its
  // finding is still selected. Open only when the selection moves onto a
  // finding.
  const [revealMemory] = useState(() => {
    let revealed: string | null = null;
    return {
      get: () => revealed,
      set: (next: string | null) => {
        revealed = next;
      },
    };
  });
  const isSectionOpen = (key: string) => !collapsedSections.has(key);
  // why: selecting a finding from the artwork (its pin) must never land on a
  // card hidden inside a closed section -- open the section that holds it.
  useEffect(() => {
    if (!selectedCommentId) {
      revealMemory.set(null);
      return;
    }
    const holder = sections.find((section) =>
      section.items.some((comment) => comment._id === selectedCommentId),
    );
    if (!holder) return;
    if (revealMemory.get() === selectedCommentId) return;
    revealMemory.set(selectedCommentId);
    setCollapsedSections((prev) => {
      if (!prev.has(holder.key)) return prev;
      const next = new Set(prev);
      next.delete(holder.key);
      return next;
    });
  }, [selectedCommentId, sections, revealMemory]);
  // why: a finding selected out of a closed section renders only once the
  // effect above opens it, so scroll when its card can be found -- not only
  // when the selection moves, or it is left off-screen.
  const selectedShown =
    selectedCommentId != null &&
    (!showSections ||
      sections.some(
        (section) =>
          isSectionOpen(section.key) &&
          section.items.some((comment) => comment._id === selectedCommentId),
      ));
  useLayoutEffect(() => {
    if (!selectedCommentId || !selectedShown) return;
    document
      .querySelector(
        `[data-testid="ask-v2-review-comment-card-${selectedCommentId}"]`,
      )
      ?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [selectedCommentId, selectedShown]);
  // The finding whose header delete opened the scope prompt. The prompt
  // renders in that card's open body, so delete on a closed card opens it.
  const [rejectingCommentId, setRejectingCommentId] = useState<string | null>(
    null,
  );
  // why: artwork selection never hits the delete handler; clearing when the selection IS this finding would hide the prompt a closed-card delete just opened
  useEffect(() => {
    setRejectingCommentId((current) =>
      current != null && current !== selectedCommentId ? null : current,
    );
  }, [selectedCommentId]);
  const toggleSection = (key: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const renderComment = (comment: CommentWithOptionalTags) => {
    const id = comment._id;
    const expanded = selectedCommentId === id;
    const title = reviewCommentTitle(comment);
    const commentNumber = commentNumbers[id] ?? 0;
    const collapsedSurface =
      "bg-puntt-neutral-gray-3 hover:bg-base-white transition-colors";
    const expandedSurface = "bg-base-white";
    // why: a demoted finding is one she already threw out. Dimmed and
    // dashed while collapsed so the list separates it at a glance (it is
    // also sorted last); full strength on hover or when opened, because
    // demote-not-drop means it must stay readable, never hidden. A finding
    // she rejects in this review gets the same look in its place and keeps
    // its number, so Restore is one click on the card away.
    // A finding she restored here is live again, so it loses the dim.
    const demoted = isRejectedBefore(comment) && comment.restoredAt == null;
    const rejectedHere = comment.liked === false;
    const demotedSurface =
      (demoted || rejectedHere) && !expanded
        ? " border-dashed opacity-60 hover:opacity-100"
        : "";
    // why: read-only viewers get no onJudge, and only AI findings write the existing `liked` field
    const canJudge =
      onJudge != null &&
      comment.isAI &&
      !comment.isPending &&
      !comment.isDeleted;
    const actionCount = canJudge ? 2 : 0;
    const rejectedHereLabel = (
      <AskV2ReviewRejectedHereSubheading
        comment={comment}
        commentId={id}
        authorNameByUserId={authorNameByUserId}
        currentUserId={currentUserId}
        currentUserName={currentUserName}
      />
    );

    return (
      <li key={id} className="mb-2">
        <div
          className={`group/comment relative overflow-hidden rounded-lg border border-puntt-neutral-gray-5 transition-opacity ${
            expanded ? expandedSurface : collapsedSurface
          }${demotedSurface}`}
          data-testid={`ask-v2-review-comment-card-${id}`}
          data-demoted={demoted ? "true" : undefined}
          data-rejected={rejectedHere ? "true" : undefined}
        >
          {/* why: the header action icons render as a sibling (absolute, top-right),
              never inside this button -- a button inside a button is invalid
              markup and swallows the inner click. */}
          <button
            type="button"
            className={`w-full p-3 text-left ${
              expanded ? expandedSurface : collapsedSurface
            }`}
            data-testid={`ask-v2-review-comment-${id}`}
            aria-expanded={expanded}
            onClick={() => {
              setRejectingCommentId(null);
              if (expanded) onClear();
              else onSelect(id);
            }}
          >
            {expanded ? (
              <>
                <CommentIdentityRow
                  id={id}
                  commentNumber={commentNumber}
                  authorLabel={reviewCommentAuthorLabel(comment, authorOptions)}
                  createdAt={comment.createdAt}
                  actionCount={actionCount}
                />
                <div className="mt-2 min-w-0 text-left">
                  <Text
                    as="p"
                    size="3"
                    weight="medium"
                    className="leading-snug text-puntt-neutral-gray-12"
                    data-testid={`ask-v2-review-comment-title-${id}`}
                  >
                    {title}
                  </Text>
                  {rejectedHereLabel}
                  <PreviouslyRejectedSubheading
                    comment={comment}
                    commentId={id}
                    currentUserId={currentUserId}
                  />
                  <PreviouslyConfirmedSubheading
                    comment={comment}
                    commentId={id}
                    currentUserId={currentUserId}
                  />
                  <FoldedIntoSubheading comment={comment} commentId={id} />
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {comment.isRequired ? (
                      <Badge
                        size="1"
                        variant="solid"
                        color="red"
                        data-testid="ask-v2-review-comment-urgent-badge"
                      >
                        {t("askV2.review.urgentBadge")}
                      </Badge>
                    ) : null}
                    <CommentSeverityBadge severity={comment.severity} />
                    <CommentTagBadges comment={comment} commentId={id} />
                  </div>
                </div>
              </>
            ) : (
              <div className="line-clamp-5 text-left">
                <CommentIdentityRow
                  id={id}
                  commentNumber={commentNumber}
                  authorLabel={reviewCommentAuthorLabel(comment, authorOptions)}
                  createdAt={comment.createdAt}
                  actionCount={actionCount}
                />
                <div className="mt-2 min-w-0">
                  <Text
                    as="p"
                    size="3"
                    weight="medium"
                    className="leading-snug text-puntt-neutral-gray-12"
                    data-testid={`ask-v2-review-comment-title-${id}`}
                  >
                    {title}
                  </Text>
                  {rejectedHereLabel}
                  <PreviouslyRejectedSubheading
                    comment={comment}
                    commentId={id}
                    currentUserId={currentUserId}
                  />
                  <PreviouslyConfirmedSubheading
                    comment={comment}
                    commentId={id}
                    currentUserId={currentUserId}
                  />
                  <FoldedIntoSubheading comment={comment} commentId={id} />
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <CommentTagBadges comment={comment} commentId={id} />
                </div>
                <CollapsedCommentPreview comment={comment} />
              </div>
            )}
          </button>
          {expanded ? (
            <div
              className="bg-base-white px-3 pb-3 pt-0"
              data-testid={`ask-v2-review-comment-expanded-${id}`}
            >
              <JudgmentReceiptLink comment={comment} commentId={id} />
              <AskPunttReviewCommentBody
                commentId={id}
                description={comment.description}
                claimCitations={claimCitationsByComment.get(id)}
                memoryCitations={memoryCitationsByComment.get(id)}
                afterExpand={
                  <AskPunttReviewCommentSource
                    comment={comment}
                    contextFiles={contextFiles}
                    reviewFiles={reviewFiles}
                  />
                }
              />
              {canJudge && rejectingCommentId === id ? (
                <AskV2ReviewRejectScopePrompt
                  commentId={id}
                  findingText={comment.description}
                  brand={citedRuleBrand(comment.citations)}
                  choices={citedRuleScopeChoices(comment.citations)}
                  onConfirm={(scope) => {
                    setRejectingCommentId(null);
                    onJudge?.(id, false, scope);
                  }}
                  onCancel={() => setRejectingCommentId(null)}
                />
              ) : null}
            </div>
          ) : null}
          {canJudge ? (
            <div className="absolute right-3 top-3.5">
              <AskV2ReviewCommentJudgmentActions
                commentId={id}
                liked={comment.liked}
                rejected={reviewCommentGroup(comment) === "rejected"}
                disabled={judgmentPending}
                pinned={
                  expanded ||
                  comment.liked != null ||
                  reviewCommentGroup(comment) === "rejected"
                }
                onJudge={(liked) => {
                  setRejectingCommentId(null);
                  onJudge?.(id, liked);
                }}
                onRequestDelete={() => {
                  setRejectingCommentId(id);
                  if (!expanded) onSelect(id);
                }}
              />
            </div>
          ) : null}
        </div>
      </li>
    );
  };

  return (
    <section
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      data-testid="ask-v2-review-comments"
      aria-label={t("askV2.review.findingsLabel")}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
        {/* why: the split sits on the title's line, so a re-review's header is
            as tall as a first review's and the cards stay level with the
            artwork pane beside it. */}
        <div
          className="flex min-w-0 items-baseline gap-2"
          data-testid="ask-v2-review-findings-title-row"
        >
          <h2
            className="shrink-0 text-[13px] font-bold text-puntt-neutral-gray-12"
            data-testid="ask-v2-review-findings-title"
          >
            {t("askV2.review.findingsTitle")}
          </h2>
          {showSections ? (
            // why: the header names what is still open, so a review's total
            // never reads as that many findings left to act on.
            <p
              className="m-0 truncate text-[12px] text-puntt-neutral-gray-10"
              data-testid="ask-v2-review-findings-split"
            >
              {t("askV2.review.findingsSplitUnresolved", {
                count: unresolvedCount,
              })}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {showTagFilter ? (
            <AskV2ReviewTagFilter
              activeTags={activeTags}
              onToggle={onToggleTag}
              sources={sourceFilter?.sources}
              hiddenSources={sourceFilter?.hidden}
              onToggleSource={sourceFilter?.onToggle}
            />
          ) : null}
        </div>
      </div>
      <ul
        className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
        data-testid="ask-v2-review-comment-list"
        aria-label={t("askV2.review.commentsLabel")}
        // why: a memory chip's card keeps inside this list, scrolling yet or not, so a short list never lets it slide over the artwork pane (`popoverBoundary`).
        data-popover-boundary
      >
        {comments.length === 0 ? (
          <li
            className="px-2 py-6 text-center text-[13px] text-puntt-neutral-gray-10"
            data-testid={emptyStateTestId}
          >
            {emptyStateCopy}
          </li>
        ) : null}
        {showSections
          ? sections.map((section) => (
              <AskV2ReviewCommentSection
                key={section.key}
                sectionKey={section.key}
                label={t(section.labelKey)}
                count={section.items.length}
                open={isSectionOpen(section.key)}
                listId={`${sectionIdBase}-${section.key}`}
                onToggle={() => toggleSection(section.key)}
              >
                {isSectionOpen(section.key)
                  ? section.items.map(renderComment)
                  : null}
              </AskV2ReviewCommentSection>
            ))
          : comments.map(renderComment)}
      </ul>
    </section>
  );
}

/**
 * A finding she judged while the review was still running, which warhol's own
 * dedup then folded into another finding when the turn ended.
 *
 * Kept rather than deleted — the judgment is hers — and labelled, so the list
 * does not read as two comments for one defect. Only ever set on a comment
 * that carries a judgment: an unjudged provisional finding is removed outright
 * (see `mergeWarholReviewComments`).
 */
function FoldedIntoSubheading({
  comment,
  commentId,
}: {
  comment: CommentWithOptionalTags;
  commentId: string;
}) {
  const { t } = useTranslation("ui");
  const folded = comment.foldedInto;
  if (!folded) return null;
  return (
    <div
      className="mt-2 rounded-md border border-puntt-neutral-gray-5 bg-puntt-neutral-gray-2 px-2.5 py-1.5"
      data-testid={`ask-v2-review-comment-folded-into-${commentId}`}
    >
      <Text
        as="p"
        size="2"
        weight="medium"
        className="flex items-center gap-1.5 text-puntt-neutral-gray-11"
      >
        <ArrowsMerge size={16} weight="bold" aria-hidden="true" />
        {t("askV2.review.foldedInto")}
      </Text>
      <Text
        as="p"
        size="1"
        color="gray"
        className="mt-0.5 line-clamp-3 whitespace-pre-line"
        data-testid={`ask-v2-review-comment-folded-into-text-${commentId}`}
      >
        {folded.description}
      </Text>
    </div>
  );
}

/**
 * Where a past decision is logged: the knowledge rule the finding cited when
 * there is one, else the review it was made on. Rendered in the open card's
 * body, not the header, because the header is a button and a link inside a
 * button is invalid markup that swallows keyboard focus.
 */
function JudgmentReceiptLink({
  comment,
  commentId,
}: {
  comment: CommentWithOptionalTags;
  commentId: string;
}) {
  const { t } = useTranslation("ui");
  const receipt = comment.previouslyRejected ?? comment.previouslyConfirmed;
  if (!receipt) return null;
  const className =
    "mb-2 inline-block text-[13px] font-medium text-puntt-blue-11 underline";
  if (receipt.ruleId) {
    return (
      <Link
        to={KNOWLEDGE_DATABASE_PATH}
        search={{
          ...KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
          tab: "rules",
          ruleId: receipt.ruleId,
        }}
        target="_blank"
        className={className}
        data-testid={`ask-v2-review-comment-judgment-rule-link-${commentId}`}
      >
        {t("assetViewer.viewJudgmentRule")}
      </Link>
    );
  }
  if (receipt.chatId && receipt.reviewId) {
    return (
      <Link
        // why: the typed route register names this path by its v1 param, `$ticketId`;
        // it is the same URL the v2 review route serves, so the link resolves there.
        to="/ask-puntt/$chatId/reviews/$ticketId"
        params={{ chatId: receipt.chatId, ticketId: receipt.reviewId }}
        target="_blank"
        className={className}
        data-testid={`ask-v2-review-comment-judgment-review-link-${commentId}`}
      >
        {t("assetViewer.viewJudgmentReview")}
      </Link>
    );
  }
  return null;
}
